/**
 * Tenant regional format settings (QL-108).
 *
 * One shared formatter used by apps/web and apps/api (PDF renderer). The stored
 * tenant settings are the ONLY input — no detection from language, browser, or
 * template. Dates are formatted by pattern substitution and numbers via a fixed
 * Intl.NumberFormat("en-US") whose separators are swapped afterwards, so output
 * is deterministic across ICU versions. `formatDate`/`formatNumber` stay
 * Intl-free; the zoned variants use `Intl.DateTimeFormat` only to read an
 * instant's wall clock in an IANA zone (numeric en-US parts, then the same
 * pattern substitution). Instants given to `formatDate`/`formatDateTime` are
 * read in the HOST zone (the browser); server-side rendering must use
 * `formatZonedDate`/`formatZonedDateTime` (QL-689).
 *
 * Display-only: stored and transported values stay ISO 8601 / raw numbers.
 */

import { parseDbTimestamp } from "./db-timestamp";
import {
  coerceTimeZone,
  DEFAULT_TENANT_TIME_ZONE,
  zonedDateParts,
  zonedDateTimeParts,
} from "./zoned-dates";

export const DATE_FORMATS = [
  "dd.MM.yyyy",
  "dd/MM/yyyy",
  "dd-MM-yyyy",
  "yyyy-MM-dd",
  "MM/dd/yyyy",
] as const;

export type DateFormat = (typeof DATE_FORMATS)[number];

export const DECIMAL_SEPARATORS = ["comma", "period"] as const;

export type DecimalSeparator = (typeof DECIMAL_SEPARATORS)[number];

export const THOUSAND_SEPARATORS = ["space", "period", "comma", "apostrophe", "none"] as const;

export type ThousandSeparator = (typeof THOUSAND_SEPARATORS)[number];

export interface RegionalFormatSettings {
  dateFormat: DateFormat;
  decimalSeparator: DecimalSeparator;
  thousandSeparator: ThousandSeparator;
  /** IANA zone the tenant's documents are dated in (QL-689), e.g. "Europe/Oslo". Server-rendered dates and times are read in this zone, never in the process zone. */
  timeZone: string;
}

/** Northern European defaults — applied to every tenant with no setup. */
export const DEFAULT_REGIONAL_FORMAT: RegionalFormatSettings = {
  dateFormat: "dd.MM.yyyy",
  decimalSeparator: "comma",
  thousandSeparator: "space",
  timeZone: DEFAULT_TENANT_TIME_ZONE,
};

/**
 * The thousand "space" is a PLAIN ASCII space — deliberately not U+00A0/U+202F,
 * which break Typst PDF rendering and text search.
 */
const DECIMAL_SEPARATOR_CHARS: Record<DecimalSeparator, string> = {
  comma: ",",
  period: ".",
};

const THOUSAND_SEPARATOR_CHARS: Record<ThousandSeparator, string> = {
  space: " ",
  period: ".",
  comma: ",",
  apostrophe: "'",
  none: "",
};

export function decimalSeparatorChar(separator: DecimalSeparator): string {
  return DECIMAL_SEPARATOR_CHARS[separator];
}

export function thousandSeparatorChar(separator: ThousandSeparator): string {
  return THOUSAND_SEPARATOR_CHARS[separator];
}

export function isDateFormat(value: string): value is DateFormat {
  return (DATE_FORMATS as readonly string[]).includes(value);
}

export function isDecimalSeparator(value: string): value is DecimalSeparator {
  return (DECIMAL_SEPARATORS as readonly string[]).includes(value);
}

export function isThousandSeparator(value: string): value is ThousandSeparator {
  return (THOUSAND_SEPARATORS as readonly string[]).includes(value);
}

/**
 * Coerce a possibly-untyped row (tenant DB row, public tenant payload) into
 * valid settings, falling back per-field to the defaults for missing or
 * out-of-allowlist values.
 */
export function coerceRegionalFormat(
  value:
    | {
        dateFormat?: string | null;
        decimalSeparator?: string | null;
        thousandSeparator?: string | null;
        timeZone?: string | null;
      }
    | null
    | undefined,
): RegionalFormatSettings {
  return {
    dateFormat:
      value?.dateFormat != null && isDateFormat(value.dateFormat)
        ? value.dateFormat
        : DEFAULT_REGIONAL_FORMAT.dateFormat,
    decimalSeparator:
      value?.decimalSeparator != null && isDecimalSeparator(value.decimalSeparator)
        ? value.decimalSeparator
        : DEFAULT_REGIONAL_FORMAT.decimalSeparator,
    thousandSeparator:
      value?.thousandSeparator != null && isThousandSeparator(value.thousandSeparator)
        ? value.thousandSeparator
        : DEFAULT_REGIONAL_FORMAT.thousandSeparator,
    timeZone: coerceTimeZone(value?.timeZone),
  };
}

export interface FormatNumberOptions {
  minimumFractionDigits?: number;
  maximumFractionDigits?: number;
}

/**
 * Fixed en-US base formatters ("1,234,567.89") — the SINGLE allowed Intl usage
 * for the regional-settings feature. Output separators are swapped afterwards,
 * never locale-derived.
 */
const baseFormatterCache = new Map<string, Intl.NumberFormat>();

function getBaseFormatter(options: FormatNumberOptions): Intl.NumberFormat {
  const min = options.minimumFractionDigits ?? 0;
  const max = Math.max(options.maximumFractionDigits ?? Math.max(min, 3), min);
  const key = `${min}:${max}`;
  let formatter = baseFormatterCache.get(key);
  if (!formatter) {
    formatter = new Intl.NumberFormat("en-US", {
      useGrouping: true,
      minimumFractionDigits: min,
      maximumFractionDigits: max,
    });
    baseFormatterCache.set(key, formatter);
  }
  return formatter;
}

/**
 * Format a number for display using the tenant's separators.
 * Returns "" for null/undefined/NaN/non-finite input.
 */
export function formatNumber(
  value: number | null | undefined,
  settings: RegionalFormatSettings,
  options: FormatNumberOptions = {},
): string {
  if (value == null || typeof value !== "number" || !Number.isFinite(value)) return "";
  const base = getBaseFormatter(options).format(value);
  const decimalChar = DECIMAL_SEPARATOR_CHARS[settings.decimalSeparator];
  const thousandChar = THOUSAND_SEPARATOR_CHARS[settings.thousandSeparator];
  // en-US output is unambiguous: "," groups, "." is the decimal point.
  const [integerPart, fractionPart] = base.split(".");
  const grouped = (integerPart as string).split(",").join(thousandChar);
  return fractionPart === undefined ? grouped : `${grouped}${decimalChar}${fractionPart}`;
}

/**
 * Parse user input typed in the tenant's format back to a raw number.
 * Accepts the tenant thousand separator (and plain spaces, which are never a
 * decimal separator) in the integer part and the tenant decimal separator.
 * Returns null for empty or invalid input.
 */
export function parseNumber(
  input: string | null | undefined,
  settings: RegionalFormatSettings,
): number | null {
  if (input == null) return null;
  const decimalChar = DECIMAL_SEPARATOR_CHARS[settings.decimalSeparator];
  const thousandChar = THOUSAND_SEPARATOR_CHARS[settings.thousandSeparator];

  let text = input.trim();
  if (text === "") return null;

  let sign = 1;
  if (text.startsWith("-") || text.startsWith("−")) {
    sign = -1;
    text = text.slice(1).trim();
  } else if (text.startsWith("+")) {
    text = text.slice(1).trim();
  }
  if (text === "") return null;

  // Split off the fraction on the configured decimal separator.
  const decimalIndex = text.lastIndexOf(decimalChar);
  let integerText = decimalIndex === -1 ? text : text.slice(0, decimalIndex);
  const fractionText = decimalIndex === -1 ? "" : text.slice(decimalIndex + decimalChar.length);

  // Strip grouping: the configured thousand separator plus plain spaces.
  if (thousandChar !== "") integerText = integerText.split(thousandChar).join("");
  integerText = integerText.split(" ").join("");

  if (integerText === "" && fractionText === "") return null;
  if (integerText !== "" && !/^\d+$/.test(integerText)) return null;
  if (fractionText !== "" && !/^\d+$/.test(fractionText)) return null;

  const normalized = `${integerText === "" ? "0" : integerText}.${fractionText === "" ? "0" : fractionText}`;
  const parsed = Number(normalized);
  if (!Number.isFinite(parsed)) return null;
  return sign * parsed;
}

interface DateParts {
  year: number;
  month: number;
  day: number;
}

const ISO_DATE_PREFIX = /^(\d{4})-(\d{2})-(\d{2})(?:$|[T ])/;

function getDateParts(value: Date | string | null | undefined): DateParts | null {
  if (value == null) return null;
  if (typeof value === "string") {
    // Date-only ISO strings are read verbatim — never routed through Date, so
    // "2026-01-01" can never shift a day across timezones.
    const match = ISO_DATE_PREFIX.exec(value);
    if (match && value.length === 10) {
      const year = Number(match[1]);
      const month = Number(match[2]);
      const day = Number(match[3]);
      // Reject calendar-invalid dates (e.g. 2026-02-31) — the UTC round-trip
      // shifts them, so components no longer match the input.
      const check = new Date(Date.UTC(year, month - 1, day));
      if (
        check.getUTCFullYear() !== year ||
        check.getUTCMonth() !== month - 1 ||
        check.getUTCDate() !== day
      ) {
        return null;
      }
      return { year, month, day };
    }
    // A zone-less datetime string ("2026-10-15 23:30:00.000", the API's
    // serialisation of every `timestamp` column) is UTC wall-clock, which
    // every JS engine would otherwise read as LOCAL time (QL-684). Strings
    // that carry a designator (Z / ±HH:MM) parse as-is.
    const parsed = parseDbTimestamp(value);
    if (!parsed) return null;
    return { year: parsed.getFullYear(), month: parsed.getMonth() + 1, day: parsed.getDate() };
  }
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return null;
    return { year: value.getFullYear(), month: value.getMonth() + 1, day: value.getDate() };
  }
  return null;
}

function pad2(value: number): string {
  return value < 10 ? `0${value}` : String(value);
}

/** Substitute `dateFormat`'s "yyyy"/"MM"/"dd" tokens with `parts`. */
function applyDatePattern(parts: DateParts, dateFormat: DateFormat): string {
  return dateFormat
    .replace("yyyy", String(parts.year).padStart(4, "0"))
    .replace("MM", pad2(parts.month))
    .replace("dd", pad2(parts.day));
}

/**
 * Format a full date by pattern substitution — no Intl.DateTimeFormat, no
 * toLocale*. Accepts a Date or an ISO 8601 string; date-only strings
 * ("yyyy-MM-dd") are formatted without timezone conversion. A zone-less
 * datetime string is read as UTC (QL-684); a zoned string or `Date` is
 * read as-is. Returns "" for invalid input.
 */
export function formatDate(
  value: Date | string | null | undefined,
  settings: RegionalFormatSettings,
): string {
  const parts = getDateParts(value);
  if (!parts) return "";
  return applyDatePattern(parts, settings.dateFormat);
}

/**
 * Format a date + time of day. Time is always 24-hour "HH:mm" — not
 * configurable. A zone-less datetime string is read as UTC (QL-684); the
 * time of day shown is the viewer's local time. Returns "" for invalid
 * input.
 */
export function formatDateTime(
  value: Date | string | null | undefined,
  settings: RegionalFormatSettings,
): string {
  if (value == null) return "";
  const date = typeof value === "string" ? parseDbTimestamp(value) : value;
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  const datePart = formatDate(date, settings);
  if (datePart === "") return "";
  return `${datePart} ${pad2(date.getHours())}:${pad2(date.getMinutes())}`;
}

/** Date-only ISO string ("yyyy-MM-dd") — a calendar date with no time of day and no zone. */
function isIsoDateOnly(value: unknown): value is string {
  return typeof value === "string" && value.length === 10 && ISO_DATE_PREFIX.test(value);
}

/**
 * Calendar parts of `value` as read in `timeZone` (month 1-based like `getDateParts`).
 * Date-only ISO strings are read verbatim — a calendar date has no zone to shift in.
 * Returns null for invalid input.
 */
export function getZonedDateParts(
  value: Date | string | null | undefined,
  timeZone: string,
): { year: number; month: number; day: number } | null {
  if (value == null) return null;
  if (isIsoDateOnly(value)) return getDateParts(value);
  const date = typeof value === "string" ? new Date(value) : value;
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return null;
  const z = zonedDateParts(date, timeZone);
  return { year: z.year, month: z.month + 1, day: z.day };
}

/** An explicit UTC designator or offset at the end of an ISO datetime's time part ("Z", "+02:00", "-0500", "+02"). */
const ISO_ZONE_SUFFIX = /(?:Z|[+-]\d{2}(?::?\d{2})?)$/i;

/**
 * Calendar parts of a CALENDAR-valued string (month 1-based), e.g. a schedule
 * start/end date. Such values reach the renderer as date-only strings or as
 * zoneless `timestamp` strings ("2026-11-01 00:00:00", Drizzle `mode: "string"`);
 * both are read from their "yyyy-MM-dd" prefix verbatim, so neither the host
 * zone nor the tenant zone can move them to the previous day or month. Only a
 * string carrying an explicit zone ("…Z", "…+02:00") is an instant, and that
 * is dated in `timeZone`. Returns null for invalid input.
 */
export function getCalendarDateParts(
  value: string | null | undefined,
  timeZone: string,
): { year: number; month: number; day: number } | null {
  if (value == null) return null;
  const match = ISO_DATE_PREFIX.exec(value);
  if (!match) return null;
  const timePart = value.slice(11);
  if (timePart !== "" && ISO_ZONE_SUFFIX.test(timePart)) {
    return getZonedDateParts(value, timeZone);
  }
  return getDateParts(value.slice(0, 10));
}

/** `formatDate`, but an instant is dated in `settings.timeZone` instead of the host zone (QL-689). "" for invalid input. */
export function formatZonedDate(
  value: Date | string | null | undefined,
  settings: RegionalFormatSettings,
): string {
  const parts = getZonedDateParts(value, settings.timeZone);
  return parts ? applyDatePattern(parts, settings.dateFormat) : "";
}

/**
 * Date + 24-hour "HH:mm" of an instant in `settings.timeZone`, no suffix, e.g. "16.10.2026 15:05".
 * A date-only ISO string ("yyyy-MM-dd") is read verbatim like `formatZonedDate` and prints "00:00" —
 * it has no time of day to shift. "" for invalid input.
 */
export function formatZonedDateTime(
  value: Date | string | null | undefined,
  settings: RegionalFormatSettings,
): string {
  if (value == null) return "";
  if (isIsoDateOnly(value)) {
    const parts = getZonedDateParts(value, settings.timeZone);
    return parts ? `${applyDatePattern(parts, settings.dateFormat)} 00:00` : "";
  }
  const date = typeof value === "string" ? new Date(value) : value;
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  const z = zonedDateTimeParts(date, settings.timeZone);
  return `${applyDatePattern({ year: z.year, month: z.month + 1, day: z.day }, settings.dateFormat)} ${pad2(z.hour)}:${pad2(z.minute)}`;
}

/** Fixed sample inputs used for the settings-UI previews. */
const SAMPLE_DATE = "2026-05-31";
const SAMPLE_NUMBER = 1234567.89;

export interface RegionalFormatOption<T extends string> {
  value: T;
  /** Rendering of a fixed sample in this format, for the settings UI. */
  sample: string;
}

/** Allowlist options with sample renderings for the settings UI. */
export const DATE_FORMAT_OPTIONS: readonly RegionalFormatOption<DateFormat>[] = DATE_FORMATS.map(
  (dateFormat) => ({
    value: dateFormat,
    sample: formatDate(SAMPLE_DATE, { ...DEFAULT_REGIONAL_FORMAT, dateFormat }),
  }),
);

export const DECIMAL_SEPARATOR_OPTIONS: readonly RegionalFormatOption<DecimalSeparator>[] =
  DECIMAL_SEPARATORS.map((decimalSeparator) => ({
    value: decimalSeparator,
    sample: formatNumber(
      SAMPLE_NUMBER,
      { ...DEFAULT_REGIONAL_FORMAT, decimalSeparator },
      { minimumFractionDigits: 2, maximumFractionDigits: 2 },
    ),
  }));

export const THOUSAND_SEPARATOR_OPTIONS: readonly RegionalFormatOption<ThousandSeparator>[] =
  THOUSAND_SEPARATORS.map((thousandSeparator) => ({
    value: thousandSeparator,
    sample: formatNumber(
      SAMPLE_NUMBER,
      { ...DEFAULT_REGIONAL_FORMAT, thousandSeparator },
      { minimumFractionDigits: 2, maximumFractionDigits: 2 },
    ),
  }));

/**
 * Live sample combining all three settings, for the settings UI.
 */
export function regionalFormatSample(settings: RegionalFormatSettings): {
  date: string;
  number: string;
} {
  return {
    date: formatDate(SAMPLE_DATE, settings),
    number: formatNumber(SAMPLE_NUMBER, settings, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }),
  };
}

export interface RegionalFormatValidationError {
  field: "dateFormat" | "decimalSeparator" | "thousandSeparator";
  /** The rejected value, or — for `sameSeparators` — the thousand separator. */
  value: string;
  /** The two separators are valid but identical. */
  sameSeparators?: true;
  message: string;
}

/**
 * Validate a candidate settings object against the allowlists and the
 * "decimal separator must differ from thousand separator" rule.
 * Returns null when valid, otherwise the first error.
 */
export function validateRegionalFormat(settings: {
  dateFormat: string;
  decimalSeparator: string;
  thousandSeparator: string;
}): RegionalFormatValidationError | null {
  if (!isDateFormat(settings.dateFormat)) {
    return {
      field: "dateFormat",
      value: settings.dateFormat,
      message: `Invalid date format: ${settings.dateFormat}`,
    };
  }
  if (!isDecimalSeparator(settings.decimalSeparator)) {
    return {
      field: "decimalSeparator",
      value: settings.decimalSeparator,
      message: `Invalid decimal separator: ${settings.decimalSeparator}`,
    };
  }
  if (!isThousandSeparator(settings.thousandSeparator)) {
    return {
      field: "thousandSeparator",
      value: settings.thousandSeparator,
      message: `Invalid thousand separator: ${settings.thousandSeparator}`,
    };
  }
  if (
    DECIMAL_SEPARATOR_CHARS[settings.decimalSeparator] ===
    THOUSAND_SEPARATOR_CHARS[settings.thousandSeparator]
  ) {
    return {
      field: "thousandSeparator",
      value: settings.thousandSeparator,
      sameSeparators: true,
      message: "Decimal separator and thousand separator must be different",
    };
  }
  return null;
}
